import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Search, Wrench, LayoutList, Columns, FileText, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import KanbanBoard from "@/components/KanbanBoard";

const statusColor = {
  // New 13-state pipeline
  new_request: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  vehicle_info_received: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  ro_insurance_pending: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  estimate_created: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  waiting_approval: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  scheduled: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  in_progress: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  calibration_completed: "bg-teal-500/20 text-teal-300 border-teal-500/30",
  report_generated: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  invoiced: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  paid: "bg-emerald-600/30 text-emerald-200 border-emerald-500/40",
  closed: "bg-slate-700/40 text-slate-300 border-slate-600",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/30",
  // Legacy fallbacks (still rendered if any old data remains)
  intake: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  ocr_processing: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  adas_analysis: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  approved: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  in_calibration: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
};

export default function Jobs() {
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("jobs_view") || "table");

  const load = () => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : "";
    api.get(`/jobs${q}`).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => { localStorage.setItem("jobs_view", view); }, [view]);

  const deleteJob = async (job, evt) => {
    evt?.preventDefault();
    evt?.stopPropagation();
    if (!window.confirm(`Excluir Job ${(job.vin || job.id).slice(0, 12)} (${job.customer_name || "sem cliente"})?\n\nA Work Order/Calibration Report vinculada também será arquivada. Isso não pode ser desfeito.`)) return;
    try {
      // Archive the linked work order (calibration report) first
      if (job.calibration_report_id) {
        try { await api.delete(`/work-orders/${job.calibration_report_id}`); } catch { /* best-effort */ }
      }
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job excluído");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const filtered = jobs.filter((j) =>
    !search || j.vin.includes(search.toUpperCase()) ||
    (j.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (j.claim_number || "").toLowerCase().includes(search.toLowerCase())
  );

  const statuses = ["", "new_request", "vehicle_info_received", "estimate_created", "waiting_approval", "scheduled", "in_progress", "calibration_completed", "invoiced", "paid", "closed"];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">{t("jobs")}</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Job Pipeline</h1>
          <p className="text-sm text-slate-400 mt-1.5">All ADAS calibration work orders across body shop partners.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex border border-slate-800 rounded-lg overflow-hidden bg-slate-900/40">
            <button
              data-testid="view-table-btn"
              onClick={() => setView("table")}
              className={`px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${view === "table" ? "bg-blue-600/20 text-blue-300" : "text-slate-400 hover:bg-slate-800/60"}`}
            >
              <LayoutList className="w-3.5 h-3.5" /> Table
            </button>
            <button
              data-testid="view-kanban-btn"
              onClick={() => setView("kanban")}
              className={`px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${view === "kanban" ? "bg-blue-600/20 text-blue-300" : "text-slate-400 hover:bg-slate-800/60"}`}
            >
              <Columns className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
          <Link to="/calibration-reports/new">
            <Button variant="outline" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10" data-testid="new-adas-report-btn">
              <FileText className="w-4 h-4 mr-2" /> New ADAS Report
            </Button>
          </Link>
          <Link to="/jobs/new-direct">
            <Button data-testid="new-job-btn" className="bg-emerald-600 hover:bg-emerald-500">
              <Sparkles className="w-4 h-4 mr-2" /> {t("new_job")}
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="jobs-search-input"
            placeholder="Search VIN, customer, claim…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#0B1120] border-slate-800 h-10"
          />
        </div>
        {view === "table" && (
          <div className="flex gap-2 overflow-x-auto">
            {statuses.map((s) => (
              <button
                key={s || "all"}
                data-testid={`jobs-filter-${s || "all"}`}
                onClick={() => setFilter(s)}
                className={`px-3 py-2 text-xs uppercase tracking-wide font-bold rounded-lg border transition-colors whitespace-nowrap ${
                  filter === s ? "bg-blue-600/20 text-blue-300 border-blue-500/40" : "bg-slate-900/40 text-slate-400 border-slate-800 hover:border-slate-600"
                }`}
              >
                {s ? s.replace(/_/g, " ") : "All"}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center text-slate-500 text-sm">Loading jobs…</div>
      ) : view === "kanban" ? (
        filtered.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-16 text-center">
            <Wrench className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <div className="text-slate-400 text-sm">{t("no_jobs")}</div>
          </div>
        ) : (
          <KanbanBoard jobs={filtered} onUpdated={load} />
        )
      ) : (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Wrench className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <div className="text-slate-400 text-sm">{t("no_jobs")}</div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">VIN</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Vehicle</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Customer</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Insurer</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Status</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Total</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.id} className="border-b border-slate-800/60 hover:bg-slate-800/20 transition-colors">
                    <td className="px-5 py-3.5 font-mono text-xs text-blue-300">
                      <Link data-testid={`job-row-${j.id}`} to={`/jobs/${j.id}`} className="hover:underline">{j.vin}</Link>
                    </td>
                    <td className="px-5 py-3.5 text-sm">{j.vehicle_year} {j.vehicle_make} {j.vehicle_model}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-300">{j.customer_name}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-300">{j.insurance_company || "—"}</td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${statusColor[j.status] || statusColor.new_request} border font-medium uppercase text-[10px] tracking-wider`}>
                        {(j.status || "new_request").replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono text-sm text-emerald-400">${(j.total_amount || 0).toFixed(2)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={(e) => deleteJob(j, e)}
                        data-testid={`delete-job-${j.id}`}
                        className="p-1.5 rounded hover:bg-rose-500/10 text-slate-500 hover:text-rose-300 transition-colors"
                        title="Excluir job + work order"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
